import { type ComponentPropsWithoutRef, forwardRef } from "react"
import { type Logo, type SolidIcon } from "./iconCore"
import { Spinner } from "./spinner"

export type BadgeVariant =
  | "live"
  | "destructive"
  | "success"
  | "warning"
  | "neutral"
  | "neutral-alt"
  | "info"
  | "info-alt"
  | "inverse"
  | "highlight"
  | "brand-gradient-solid"

export type BadgeProps = ComponentPropsWithoutRef<"span"> & {
  /**
   * Indicates the colour/variant of the Badge
   */
  variant?: BadgeVariant

  /**
   * Indicates the size of the `Badge`
   *
   * @default sm
   */
  size?: "sm" | "lg"

  /**
   * Indicates whether to apply an uppercase text transform
   */
  uppercase?: boolean

  /**
   * An icon to be shown in the inner left of the badge
   */
  icon?: SolidIcon | Logo | "dot"

  /**
   * Indicates whether to display a loading spinner within the badge.
   * This spinner will replace any icon that's being show with the `icon` prop.
   *
   * @default undefined
   */
  loading?: boolean
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  // eslint-disable-next-line @typescript-eslint/no-shadow
  (
    {
      className,
      children,
      variant,
      size,
      uppercase,
      icon: Icon,
      loading,
      ...props
    },
    ref,
  ) => {
    return (
      <span
        ref={ref}
        className={`badge ${className ?? ""}`}
        data-variant={variant}
        data-size={size}
        data-uppercase={uppercase}
        data-icon={Icon !== undefined && Icon !== "dot"}
        data-dot={Icon === "dot"}
        data-loading={loading === true}
        {...props}
      >
        {Icon && Icon !== "dot" && !loading && <Icon data-size="xs" />}
        <div className="dot" />
        {loading && (
          <div className="spinner-container">
            <Spinner data-size="xs" />
          </div>
        )}
        {children}
      </span>
    )
  },
)

Badge.displayName = "Badge"
